import { DestroyRef, Injectable, NgZone, Signal, computed, inject, signal } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import moment, { Moment } from 'moment';
import { DateService } from '@shared/services/date.service';

/** Standaard tick van de live-weergave: 1 seconde (gelijk aan `DateService.date$`). */
export const HD_TRANSIT_VIEW_TIME_DEFAULT_TICK_MS = 1000;
/** Minimale tick — beschermt tegen zinloos dure herteken-intervallen. */
export const HD_TRANSIT_VIEW_TIME_MIN_TICK_MS = 250;

/**
 * Klok voor de transits-weergave (voorbereid op de tijdmachine uit stap 4).
 *
 * - **Live (default)**: {@link viewTime} volgt de gedeelde `DateService.date$`
 *   (1s-tick). `DateService.date$` levert *lokale* Moments; alle tijd-vergelijking
 *   met de UTC-transitdata gebeurt op epoch-ms, dus dat is veilig.
 * - **Instelbare tick**: {@link setTickInterval} herstart de live-bron met een
 *   eigen `timer` — de gedeelde DateService wordt nooit afgeschilderd of
 *   gemuteerd. Besluit 6: de tick hertekent alleen; herbevragen gebeurt apart
 *   (`TransitDataService.refreshNowNextIfNeeded`).
 * - **Pin/unpin** (tijdmachine): gepind bevriest {@link viewTime} op het gegeven
 *   moment; de live-bron blijft onder water doorlopen zodat {@link unpin}
 *   direct terugspringt op "nu".
 *
 * De live-subscription wordt opgeruimd via de `DestroyRef` van de service.
 */
@Injectable({ providedIn: 'root' })
export class TransitViewTimeService {
  private readonly dateService = inject(DateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  private readonly _liveTime = signal<Moment>(moment());
  private readonly _pinnedTime = signal<Moment | null>(null);

  private liveSubscription: Subscription | null = null;
  private currentTickMs = HD_TRANSIT_VIEW_TIME_DEFAULT_TICK_MS;

  /** Effectieve weergavetijd: de gepinde tijd, of live als er niet gepind is. */
  readonly viewTime: Signal<Moment> = computed(() => this._pinnedTime() ?? this._liveTime());
  /** true zolang er niet gepind is (de weergave loopt mee met de klok). */
  readonly isLive: Signal<boolean> = computed(() => this._pinnedTime() == null);

  constructor() {
    this.startLiveSource();
    this.destroyRef.onDestroy(() => this.stopLiveSource());
  }

  /**
   * Wijzigt de live-tick (min 250 ms, default 1000 ms) en herstart de
   * live-bron. De default-tick volgt `DateService.date$`; elke afwijkende
   * tick gebruikt een eigen `timer`.
   */
  setTickInterval(ms: number): void {
    const next = Math.max(HD_TRANSIT_VIEW_TIME_MIN_TICK_MS, Math.floor(ms));
    if (next === this.currentTickMs) return;
    this.currentTickMs = next;
    this.startLiveSource();
  }

  /** De huidige tick in ms (na clamping), handig voor settings-UI en tests. */
  getTickIntervalMs(): number {
    return this.currentTickMs;
  }

  /** Bevriest de weergavetijd (defensief gekloond — Moment is mutabel). */
  pin(time: Moment): void {
    this._pinnedTime.set(moment(time));
  }

  /** Heft de pin op; de weergave springt direct terug op de live-tijd. */
  unpin(): void {
    this._pinnedTime.set(null);
  }

  private startLiveSource(): void {
    this.stopLiveSource();
    const source$ =
      this.currentTickMs === HD_TRANSIT_VIEW_TIME_DEFAULT_TICK_MS
        ? this.dateService.date$
        : timer(0, this.currentTickMs).pipe(map(() => moment()));
    // Buiten de Angular-zone: `date$` is koud (elke subscriber start hier zijn
    // eigen interval), dus de subscribe-zone bepaalt waar de timer tikt. In de
    // zone zou elke tick een app-brede change detection triggeren; de
    // signal-write markeert de OnPush-consumers zelf al (hybride scheduler).
    this.liveSubscription = this.ngZone.runOutsideAngular(() =>
      source$.subscribe(now => this._liveTime.set(now)),
    );
  }

  private stopLiveSource(): void {
    this.liveSubscription?.unsubscribe();
    this.liveSubscription = null;
  }
}
